import { Ionicons } from '@expo/vector-icons';
import { useIsFocused } from '@react-navigation/native';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Alert,
  FlatList,
  I18nManager,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';

import {
  DeviceType,
  getAllDeviceTypes,
  getDeviceTypeIcon,
  getDeviceTypeLabel,
} from '../constants/deviceTypes';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { alarmRulesApi } from '../services/alarmRulesApi';
import { deviceApi } from '../services/api';
import { getErrorMessage, handleError, parseError } from '../utils/errorHandler';
import logger from '../utils/logger';
import { canManageDevices } from '../utils/permissions';
import { useHeaderHeight } from '@react-navigation/elements';
import {
  calculateCylindricalVolume,
  litersToGallons,
  litersToM3,
  type TankShape,
} from '../utils/tankCalculations';

interface EditDeviceScreenProps {
  route: {
    params: {
      deviceId: string;
      deviceName?: string;
      from?: string;
    };
  };
  navigation: any;
}

export default function EditDeviceScreen({ route, navigation }: EditDeviceScreenProps) {
  // Header is transparent (see stack navigator) — push content below it
  const headerHeight = useHeaderHeight();
  // P2: Validate route params
  if (!route?.params?.deviceId) {
    Alert.alert('Error', 'Device information is missing', [
      { text: 'OK', onPress: () => navigation.goBack() },
    ]);
    return <View style={{ flex: 1 }} />;
  }

  const { deviceId } = route.params;
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [deviceType, setDeviceType] = useState<DeviceType>(DeviceType.MULTI_SENSOR);
  const [hasBattery, setHasBattery] = useState(false);
  const [zoneId, setZoneId] = useState<string | null>(null);
  const [siteId, setSiteId] = useState<string | null>(null);
  const [calibrationOffsetTemp, setCalibrationOffsetTemp] = useState('0');
  const [calibrationOffsetHumidity, setCalibrationOffsetHumidity] = useState('0');
  const [tankCapacityLiters, setTankCapacityLiters] = useState('');
  const [tankHeightCm, setTankHeightCm] = useState('');
  const [tankDiameterCm, setTankDiameterCm] = useState('');
  const [tankUnit, setTankUnit] = useState<'L' | 'm3'>('L');
  const [tankAlertsEnabled, setTankAlertsEnabled] = useState(false);
  const [tankAlertLowCm, setTankAlertLowCm] = useState('');
  const [tankAlertHighCm, setTankAlertHighCm] = useState('');
  const [tankRuleId, setTankRuleId] = useState<string | null>(null);
  const [showDeviceTypePicker, setShowDeviceTypePicker] = useState(false);

  // P0: focus guard + throttle — prevent dual-Modal crash on iOS 17 (RNSScreen.mm:1968)
  const isFocused = useIsFocused();
  const isPresentingRef = useRef(false);

  const openDeviceTypePicker = () => {
    if (!isFocused || isPresentingRef.current) return;
    isPresentingRef.current = true;
    setShowDeviceTypePicker(true);
    setTimeout(() => {
      isPresentingRef.current = false;
    }, 600);
  };

  // New: Auto-calculate volume state
  const [autoCalculateVolume, setAutoCalculateVolume] = useState(true);
  const [tankShape, setTankShape] = useState<TankShape>('cylindrical');
  const [tankWidthCm, setTankWidthCm] = useState('');
  const [tankDepthCm, setTankDepthCm] = useState('');

  const isArabic = i18n.language === 'ar';
  const isRTL = I18nManager.isRTL || isArabic;
  const hasPermission = user?.roles ? canManageDevices(user.roles) : false;

  // Input validation helper
  const handleNumericInput = (text: string, setter: (val: string) => void, max?: number) => {
    const cleaned = text.replace(/[^0-9.]/g, '');
    const num = parseFloat(cleaned);
    if (max && num > max) return;
    if (cleaned.startsWith('-')) return; // No negative numbers
    setter(cleaned);
  };

  const handleSignedDecimalInput = (text: string, setter: (val: string) => void) => {
    let cleaned = text.replace(/[^0-9.-]/g, '');

    const isNegative = cleaned.startsWith('-');
    cleaned = cleaned.replace(/-/g, '');
    const parts = cleaned.split('.');
    const integerPart = parts.shift() ?? '';
    const decimalPart = parts.join('');
    const normalized = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;

    if (text === '-' || (isNegative && normalized.length === 0)) {
      setter('-');
      return;
    }

    setter(`${isNegative ? '-' : ''}${normalized}`);
  };

  // Calculate rectangular tank volume
  const calculateRectangularVolume = (height: number, width: number, depth: number): number => {
    // Volume = height × width × depth (all in cm)
    const volumeCm3 = height * width * depth;
    // Convert cm³ to liters (1000 cm³ = 1 liter)
    return volumeCm3 / 1000;
  };
  const isTankDevice =
    deviceType === DeviceType.LEVEL ||
    deviceType === DeviceType.WATER ||
    deviceType === DeviceType.DIESEL ||
    name.toLowerCase().includes('tank') ||
    name.toLowerCase().includes('diesel') ||
    name.toLowerCase().includes('water');

  useEffect(() => {
    navigation.setOptions({
      title: isArabic ? 'تعديل الجهاز' : 'Edit Device',
      headerTransparent: true, // Maintain transparency for Galaxy background
      headerTintColor: '#ffffff', // Force white text for Galaxy header
      headerStyle: {
        backgroundColor: 'transparent',
        elevation: 0,
        shadowOpacity: 0,
      },
    });

    if (!hasPermission) {
      Alert.alert(
        isArabic ? 'صلاحية مطلوبة' : 'Permission Required',
        isArabic
          ? 'لا تملك صلاحية للوصول لهذه الصفحة'
          : 'You do not have permission to access this page.',
        [{ text: isArabic ? 'حسناً' : 'OK', onPress: () => navigation.goBack() }],
      );
      return;
    }

    loadDeviceData();
  }, [hasPermission]);

  // Auto-calculate volume when dimensions change
  useEffect(() => {
    if (!autoCalculateVolume) return;

    try {
      const height = parseFloat(tankHeightCm);

      if (tankShape === 'cylindrical') {
        const diameter = parseFloat(tankDiameterCm);
        if (height > 0 && diameter > 0) {
          const calculatedVolume = calculateCylindricalVolume(height, diameter);
          setTankCapacityLiters(calculatedVolume.toFixed(2));
          logger.info('Auto-calculated cylindrical tank volume:', calculatedVolume);
        }
      } else if (tankShape === 'rectangular') {
        const width = parseFloat(tankWidthCm);
        const depth = parseFloat(tankDepthCm);
        if (height > 0 && width > 0 && depth > 0) {
          const calculatedVolume = calculateRectangularVolume(height, width, depth);
          setTankCapacityLiters(calculatedVolume.toFixed(2));
          logger.info('Auto-calculated rectangular tank volume:', calculatedVolume);
        }
      }
    } catch (error) {
      logger.warn('Failed to auto-calculate volume:', error);
    }
  }, [tankHeightCm, tankDiameterCm, tankWidthCm, tankDepthCm, autoCalculateVolume, tankShape]);

  const loadDeviceData = async () => {
    try {
      setLoading(true);

      if (!user?.orgId) {
        throw new Error('Organization ID not found');
      }

      // Load all devices for the organization
      const devices = await deviceApi.getDevices(user.orgId);

      // Find the specific device
      const device = devices.find((d: any) => d.id === deviceId);

      if (!device) {
        throw new Error('Device not found');
      }

      // Update state with device data
      setName(device.name);
      setDeviceType(device.deviceType);
      setHasBattery(device.hasBattery || false);
      setZoneId(device.zoneId || null);
      setSiteId(device.siteId || null);
      setCalibrationOffsetTemp(device.calibrationOffsetC?.toString() || '0');
      setCalibrationOffsetHumidity(device.calibrationOffsetH?.toString() || '0');
      setTankCapacityLiters(device.tankCapacityLiters?.toString() || '');
      setTankHeightCm(device.tankHeightCm?.toString() || '');
      setTankDiameterCm(device.tankDiameterCm?.toString() || '');
      setTankWidthCm(device.tankWidthCm?.toString() || '');
      setTankDepthCm(device.tankDepthCm?.toString() || '');
      setTankShape(device.tankShape || 'cylindrical');
      setTankUnit(device.tankUnit === 'm3' ? 'm3' : 'L');

      try {
        const rules = await alarmRulesApi.getDeviceRules(deviceId);
        const levelRule = rules.find((r) => r.type === 'level');
        if (levelRule) {
          setTankAlertsEnabled(true);
          setTankRuleId(levelRule.id);
          setTankAlertLowCm(levelRule.thresholdLow?.toString() || '');
          setTankAlertHighCm(levelRule.thresholdHigh?.toString() || '');
        }
      } catch (rulesError) {
        logger.warn('Failed to load tank rules:', rulesError);
      }

      logger.info('Device data loaded:', device);
    } catch (error) {
      handleError(error, 'EditDevice.loadDeviceData', {
        language: isArabic ? 'ar' : 'en',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'الرجاء إدخال اسم الجهاز' : 'Please enter device name',
      );
      return;
    }

    try {
      setSaving(true);

      const tankData: any = {};
      if (isTankDevice) {
        if (tankCapacityLiters.trim()) {
          tankData.tankCapacityLiters = parseFloat(tankCapacityLiters);
        }
        if (tankHeightCm.trim()) {
          tankData.tankHeightCm = parseFloat(tankHeightCm);
        }
        tankData.tankShape = tankShape;
        if (tankShape === 'cylindrical') {
          if (tankDiameterCm.trim()) {
            tankData.tankDiameterCm = parseFloat(tankDiameterCm);
          }
          tankData.tankWidthCm = null;
          tankData.tankDepthCm = null;
        } else if (tankShape === 'rectangular') {
          if (tankWidthCm.trim()) {
            tankData.tankWidthCm = parseFloat(tankWidthCm);
          }
          if (tankDepthCm.trim()) {
            tankData.tankDepthCm = parseFloat(tankDepthCm);
          }
          tankData.tankDiameterCm = null;
        }
        if (tankUnit) {
          tankData.tankUnit = tankUnit;
        }
      }

      const updateData = {
        name: name.trim(),
        deviceType: deviceType,
        hasBattery: hasBattery,
        zoneId: zoneId,
        calibrationOffsetC: parseFloat(calibrationOffsetTemp) || 0,
        calibrationOffsetH: parseFloat(calibrationOffsetHumidity) || 0,
        ...tankData,
      };

      logger.info('Updating device:', { deviceId, updateData });

      // Save device data via API
      const result = await deviceApi.updateDevice(deviceId, updateData);

      logger.info('Device updated successfully:', result);

      const low = tankAlertLowCm.trim() ? parseFloat(tankAlertLowCm) : undefined;
      const high = tankAlertHighCm.trim() ? parseFloat(tankAlertHighCm) : undefined;

      if (isTankDevice) {
        if (tankAlertsEnabled && (low !== undefined || high !== undefined)) {
          try {
            if (tankRuleId) {
              await alarmRulesApi.updateRule(tankRuleId, {
                type: 'level',
                severity: 'warning',
                thresholdLow: low,
                thresholdHigh: high,
              });
            } else {
              const rule = await alarmRulesApi.createRule({
                deviceId,
                type: 'level',
                severity: 'warning',
                thresholdLow: low,
                thresholdHigh: high,
              });
              setTankRuleId(rule.id);
            }
          } catch (ruleError) {
            logger.error('Failed to save tank rule:', ruleError);
          }
        } else if (tankRuleId && !tankAlertsEnabled) {
          try {
            await alarmRulesApi.deleteRule(tankRuleId);
            setTankRuleId(null);
          } catch (ruleError) {
            logger.error('Failed to delete tank rule:', ruleError);
          }
        }
      }

      setShowDeviceTypePicker(false);
      Alert.alert(
        isArabic ? 'نجح' : 'Success',
        isArabic ? 'تم حفظ التغييرات بنجاح' : 'Changes saved successfully',
        [
          {
            text: isArabic ? 'موافق' : 'OK',
            onPress: () => {
              if (navigation.canGoBack()) {
                navigation.goBack();
              }
            },
          },
        ],
      );
    } catch (error) {
      const appError = parseError(error);
      logger.error('Failed to save device:', appError);
      Alert.alert(isArabic ? 'خطأ' : 'Error', getErrorMessage(appError, isArabic ? 'ar' : 'en'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <View
      style={[
        styles.container,
        { backgroundColor: theme.colors.background, paddingTop: headerHeight },
      ]}
    >
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={[styles.loadingText, { color: theme.colors.text }]}>
            {isArabic ? 'جاري التحميل...' : 'Loading...'}
          </Text>
        </View>
      ) : (
        <>
          <ScrollView>
            <View style={styles.content}>
              {/* Device Name */}
              <View style={styles.fieldContainer}>
                <Text style={[styles.label, { color: theme.colors.text }]}>
                  {isArabic ? 'اسم الجهاز' : 'Device Name'}
                </Text>
                <TextInput
                  style={[
                    styles.input,
                    {
                      backgroundColor: theme.colors.card,
                      color: theme.colors.text,
                      borderColor: theme.colors.border,
                      textAlign: isRTL ? 'right' : 'left',
                    },
                  ]}
                  value={name}
                  onChangeText={setName}
                  placeholder={isArabic ? 'أدخل اسم الجهاز' : 'Enter device name'}
                  placeholderTextColor={theme.colors.textSecondary}
                />
              </View>

              {/* Device Type */}
              <View style={styles.fieldContainer}>
                <Text style={[styles.label, { color: theme.colors.text }]}>
                  {isArabic ? 'نوع الجهاز' : 'Device Type'}
                </Text>
                <TouchableOpacity
                  style={[
                    styles.input,
                    styles.pickerButton,
                    { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
                  ]}
                  onPress={openDeviceTypePicker}
                >
                  <View style={styles.pickerContent}>
                    <Ionicons
                      name={getDeviceTypeIcon(deviceType) as any}
                      size={20}
                      color={theme.colors.primary}
                    />
                    <Text style={[styles.pickerText, { color: theme.colors.text }]}>
                      {getDeviceTypeLabel(deviceType, isArabic ? 'ar' : 'en')}
                    </Text>
                  </View>
                  <Ionicons name="chevron-down" size={20} color={theme.colors.textSecondary} />
                </TouchableOpacity>
              </View>

              {/* Has Battery */}
              <View
                style={[
                  styles.switchContainer,
                  { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
                ]}
              >
                <View style={styles.switchLabel}>
                  <Ionicons name="battery-half" size={24} color={theme.colors.primary} />
                  <View style={styles.switchTextContainer}>
                    <Text style={[styles.label, { marginBottom: 0, color: theme.colors.text }]}>
                      {isArabic ? 'يحتوي على بطارية' : 'Has Battery'}
                    </Text>
                    <Text
                      style={[styles.hint, { marginTop: 2, color: theme.colors.textSecondary }]}
                    >
                      {isArabic
                        ? 'الجهاز يعمل بالبطارية ويرسل مستوى الشحن'
                        : 'Device is battery powered and reports battery level'}
                    </Text>
                  </View>
                </View>
                <Switch
                  value={hasBattery}
                  onValueChange={setHasBattery}
                  trackColor={{ false: theme.colors.border, true: theme.colors.primary + '80' }}
                  thumbColor={hasBattery ? theme.colors.primary : theme.colors.textSecondary}
                />
              </View>

              {/* Calibration Settings */}
              <View
                style={[
                  styles.section,
                  { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
                ]}
              >
                <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
                  {isArabic ? 'معايرة الحساس' : 'Sensor Calibration'}
                </Text>
                <Text style={[styles.sectionDescription, { color: theme.colors.textSecondary }]}>
                  {isArabic
                    ? 'يمكنك تعديل قيم المعايرة لضبط دقة القراءات'
                    : 'Adjust calibration values to improve reading accuracy'}
                </Text>

                {isTankDevice && (
                  <View
                    style={[
                      styles.tankConfigCard,
                      {
                        backgroundColor: theme.colors.background,
                        borderColor: theme.colors.border,
                      },
                    ]}
                  >
                    <View style={styles.tankConfigHeader}>
                      <Ionicons name="water" size={20} color={theme.colors.info} />
                      <Text style={[styles.tankConfigTitle, { color: theme.colors.text }]}>
                        {isArabic ? 'إعدادات الخزان' : 'Tank Settings'}
                      </Text>
                    </View>

                    {/* Auto-Calculate Toggle */}
                    <View
                      style={[
                        styles.switchContainer,
                        {
                          backgroundColor: theme.colors.card,
                          borderColor: theme.colors.border,
                          marginBottom: 12,
                        },
                      ]}
                    >
                      <View style={styles.switchLabel}>
                        <Ionicons name="calculator" size={20} color={theme.colors.success} />
                        <View style={styles.switchTextContainer}>
                          <Text
                            style={[styles.label, { marginBottom: 0, color: theme.colors.text }]}
                          >
                            {isArabic ? 'حساب الحجم تلقائياً' : 'Auto-Calculate Volume'}
                          </Text>
                          <Text
                            style={[
                              styles.hint,
                              { marginTop: 2, color: theme.colors.textSecondary },
                            ]}
                          >
                            {isArabic
                              ? 'يحسب السعة من الأبعاد تلقائياً'
                              : 'Calculate capacity from dimensions automatically'}
                          </Text>
                        </View>
                      </View>
                      <Switch
                        value={autoCalculateVolume}
                        onValueChange={setAutoCalculateVolume}
                        trackColor={{
                          false: theme.colors.border,
                          true: theme.colors.success + '80',
                        }}
                        thumbColor={
                          autoCalculateVolume ? theme.colors.success : theme.colors.textSecondary
                        }
                      />
                    </View>

                    {/* Tank Shape Selector (for future: currently only cylindrical) */}
                    {autoCalculateVolume && (
                      <View style={styles.fieldContainerSmall}>
                        <Text style={[styles.label, { color: theme.colors.text }]}>
                          {isArabic ? 'شكل الخزان' : 'Tank Shape'}
                        </Text>
                        <View style={styles.unitToggleRow}>
                          <TouchableOpacity
                            style={[
                              styles.unitToggle,
                              { borderColor: theme.colors.border, flex: 1 },
                              tankShape === 'cylindrical' && {
                                backgroundColor: theme.colors.primary,
                              },
                            ]}
                            onPress={() => setTankShape('cylindrical')}
                          >
                            <Ionicons
                              name="git-commit-outline"
                              size={16}
                              color={tankShape === 'cylindrical' ? '#fff' : theme.colors.text}
                            />
                            <Text
                              style={[
                                styles.unitToggleText,
                                {
                                  color: tankShape === 'cylindrical' ? '#fff' : theme.colors.text,
                                  marginLeft: 4,
                                },
                              ]}
                            >
                              {isArabic ? 'أسطواني' : 'Cylindrical'}
                            </Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[
                              styles.unitToggle,
                              { borderColor: theme.colors.border, flex: 1 },
                              tankShape === 'rectangular' && {
                                backgroundColor: theme.colors.primary,
                              },
                            ]}
                            onPress={() => setTankShape('rectangular')}
                          >
                            <Ionicons
                              name="square-outline"
                              size={16}
                              color={tankShape === 'rectangular' ? '#fff' : theme.colors.text}
                            />
                            <Text
                              style={[
                                styles.unitToggleText,
                                {
                                  color: tankShape === 'rectangular' ? '#fff' : theme.colors.text,
                                  marginLeft: 4,
                                },
                              ]}
                            >
                              {isArabic ? 'مستطيل' : 'Rectangular'}
                            </Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    )}

                    {/* Dimensions */}
                    <View style={styles.row}>
                      <View style={[styles.fieldContainerSmall, styles.rowItem]}>
                        <Text style={[styles.label, { color: theme.colors.text }]}>
                          {isArabic ? 'ارتفاع الخزان (سم)' : 'Tank Height (cm)'}
                        </Text>
                        <TextInput
                          style={[
                            styles.input,
                            {
                              backgroundColor: theme.colors.card,
                              color: theme.colors.text,
                              borderColor: theme.colors.border,
                              textAlign: isRTL ? 'right' : 'left',
                            },
                          ]}
                          value={tankHeightCm}
                          onChangeText={(text) => handleNumericInput(text, setTankHeightCm, 1000)}
                          placeholder={isArabic ? 'مثال: 150' : 'e.g., 150'}
                          placeholderTextColor={theme.colors.textSecondary}
                          keyboardType="numeric"
                        />
                      </View>

                      {tankShape === 'cylindrical' ? (
                        <View style={[styles.fieldContainerSmall, styles.rowItem]}>
                          <Text style={[styles.label, { color: theme.colors.text }]}>
                            {isArabic ? 'قطر الخزان (سم)' : 'Tank Diameter (cm)'}
                          </Text>
                          <TextInput
                            style={[
                              styles.input,
                              {
                                backgroundColor: theme.colors.card,
                                color: theme.colors.text,
                                borderColor: theme.colors.border,
                                textAlign: isRTL ? 'right' : 'left',
                              },
                            ]}
                            value={tankDiameterCm}
                            onChangeText={(text) =>
                              handleNumericInput(text, setTankDiameterCm, 1000)
                            }
                            placeholder={isArabic ? 'مثال: 120' : 'e.g., 120'}
                            placeholderTextColor={theme.colors.textSecondary}
                            keyboardType="numeric"
                          />
                        </View>
                      ) : (
                        <View style={[styles.fieldContainerSmall, styles.rowItem]}>
                          <Text style={[styles.label, { color: theme.colors.text }]}>
                            {isArabic ? 'عرض الخزان (سم)' : 'Tank Width (cm)'}
                          </Text>
                          <TextInput
                            style={[
                              styles.input,
                              {
                                backgroundColor: theme.colors.card,
                                color: theme.colors.text,
                                borderColor: theme.colors.border,
                                textAlign: isRTL ? 'right' : 'left',
                              },
                            ]}
                            value={tankWidthCm}
                            onChangeText={(text) => handleNumericInput(text, setTankWidthCm, 1000)}
                            placeholder={isArabic ? 'مثال: 100' : 'e.g., 100'}
                            placeholderTextColor={theme.colors.textSecondary}
                            keyboardType="numeric"
                          />
                        </View>
                      )}
                    </View>

                    {tankShape === 'rectangular' && (
                      <View style={styles.fieldContainerSmall}>
                        <Text style={[styles.label, { color: theme.colors.text }]}>
                          {isArabic ? 'عمق الخزان (سم)' : 'Tank Depth (cm)'}
                        </Text>
                        <TextInput
                          style={[
                            styles.input,
                            {
                              backgroundColor: theme.colors.card,
                              color: theme.colors.text,
                              borderColor: theme.colors.border,
                              textAlign: isRTL ? 'right' : 'left',
                            },
                          ]}
                          value={tankDepthCm}
                          onChangeText={(text) => handleNumericInput(text, setTankDepthCm, 1000)}
                          placeholder={isArabic ? 'مثال: 80' : 'e.g., 80'}
                          placeholderTextColor={theme.colors.textSecondary}
                          keyboardType="numeric"
                        />
                      </View>
                    )}

                    {/* Calculated Volume Display - Cylindrical */}
                    {autoCalculateVolume &&
                      tankShape === 'cylindrical' &&
                      tankHeightCm &&
                      tankDiameterCm &&
                      parseFloat(tankHeightCm) > 0 &&
                      parseFloat(tankDiameterCm) > 0 && (
                        <View
                          style={[
                            styles.calculatedValueCard,
                            {
                              backgroundColor: theme.colors.success + '10',
                              borderColor: theme.colors.success + '30',
                            },
                          ]}
                        >
                          <View style={styles.calculatedValueHeader}>
                            <Ionicons
                              name="checkmark-circle"
                              size={20}
                              color={theme.colors.success}
                            />
                            <Text style={[styles.calculatedLabel, { color: theme.colors.success }]}>
                              {isArabic ? 'الحجم المحسوب:' : 'Calculated Volume:'}
                            </Text>
                          </View>
                          <View style={styles.calculatedValueRow}>
                            <Text style={[styles.calculatedValue, { color: theme.colors.text }]}>
                              {parseFloat(tankCapacityLiters || '0').toFixed(2)}{' '}
                              {isArabic ? 'لتر' : 'L'}
                            </Text>
                            <Text
                              style={[
                                styles.calculatedValueSecondary,
                                { color: theme.colors.textSecondary },
                              ]}
                            >
                              ({litersToGallons(parseFloat(tankCapacityLiters || '0')).toFixed(2)}{' '}
                              {isArabic ? 'جالون' : 'gal'} |{' '}
                              {litersToM3(parseFloat(tankCapacityLiters || '0')).toFixed(3)} m³)
                            </Text>
                          </View>
                        </View>
                      )}

                    {/* Calculated Volume Display - Rectangular */}
                    {autoCalculateVolume &&
                      tankShape === 'rectangular' &&
                      tankHeightCm &&
                      tankWidthCm &&
                      tankDepthCm &&
                      parseFloat(tankHeightCm) > 0 &&
                      parseFloat(tankWidthCm) > 0 &&
                      parseFloat(tankDepthCm) > 0 && (
                        <View
                          style={[
                            styles.calculatedValueCard,
                            {
                              backgroundColor: theme.colors.success + '10',
                              borderColor: theme.colors.success + '30',
                            },
                          ]}
                        >
                          <View style={styles.calculatedValueHeader}>
                            <Ionicons
                              name="checkmark-circle"
                              size={20}
                              color={theme.colors.success}
                            />
                            <Text style={[styles.calculatedLabel, { color: theme.colors.success }]}>
                              {isArabic ? 'الحجم المحسوب:' : 'Calculated Volume:'}
                            </Text>
                          </View>
                          <View style={styles.calculatedValueRow}>
                            <Text style={[styles.calculatedValue, { color: theme.colors.text }]}>
                              {parseFloat(tankCapacityLiters || '0').toFixed(2)}{' '}
                              {isArabic ? 'لتر' : 'L'}
                            </Text>
                            <Text
                              style={[
                                styles.calculatedValueSecondary,
                                { color: theme.colors.textSecondary },
                              ]}
                            >
                              ({litersToGallons(parseFloat(tankCapacityLiters || '0')).toFixed(2)}{' '}
                              {isArabic ? 'جالون' : 'gal'} |{' '}
                              {litersToM3(parseFloat(tankCapacityLiters || '0')).toFixed(3)} m³)
                            </Text>
                          </View>
                        </View>
                      )}

                    {/* Manual Capacity Input (shown when auto-calculate is off) */}
                    <View style={styles.fieldContainerSmall}>
                      <View style={styles.labelWithIcon}>
                        <Text style={[styles.label, { color: theme.colors.text }]}>
                          {isArabic ? 'سعة الخزان (لتر)' : 'Tank Capacity (L)'}
                        </Text>
                        {autoCalculateVolume && (
                          <View
                            style={[
                              styles.autoCalculateBadge,
                              { backgroundColor: theme.colors.success + '20' },
                            ]}
                          >
                            <Ionicons name="flash" size={12} color={theme.colors.success} />
                            <Text
                              style={[styles.autoCalculateText, { color: theme.colors.success }]}
                            >
                              {isArabic ? 'تلقائي' : 'Auto'}
                            </Text>
                          </View>
                        )}
                      </View>
                      <TextInput
                        style={[
                          styles.input,
                          {
                            backgroundColor: autoCalculateVolume
                              ? theme.colors.border + '20'
                              : theme.colors.card,
                            color: theme.colors.text,
                            borderColor: theme.colors.border,
                          },
                        ]}
                        value={tankCapacityLiters}
                        onChangeText={setTankCapacityLiters}
                        placeholder={isArabic ? 'مثال: 2000' : 'e.g., 2000'}
                        placeholderTextColor={theme.colors.textSecondary}
                        keyboardType="numeric"
                        editable={!autoCalculateVolume}
                      />
                    </View>

                    <View style={styles.fieldContainerSmall}>
                      <Text style={[styles.label, { color: theme.colors.text }]}>
                        {isArabic ? 'وحدة السعة' : 'Capacity Unit'}
                      </Text>
                      <View style={styles.unitToggleRow}>
                        <TouchableOpacity
                          style={[
                            styles.unitToggle,
                            { borderColor: theme.colors.border },
                            tankUnit === 'L' && { backgroundColor: theme.colors.primary },
                          ]}
                          onPress={() => setTankUnit('L')}
                        >
                          <Text
                            style={[
                              styles.unitToggleText,
                              { color: tankUnit === 'L' ? '#fff' : theme.colors.text },
                            ]}
                          >
                            L
                          </Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[
                            styles.unitToggle,
                            { borderColor: theme.colors.border },
                            tankUnit === 'm3' && { backgroundColor: theme.colors.primary },
                          ]}
                          onPress={() => setTankUnit('m3')}
                        >
                          <Text
                            style={[
                              styles.unitToggleText,
                              { color: tankUnit === 'm3' ? '#fff' : theme.colors.text },
                            ]}
                          >
                            m³
                          </Text>
                        </TouchableOpacity>
                      </View>
                    </View>

                    <View style={styles.switchRow}>
                      <View style={styles.switchLabelInline}>
                        <Ionicons name="warning-outline" size={20} color={theme.colors.warning} />
                        <Text style={[styles.label, { marginBottom: 0, color: theme.colors.text }]}>
                          {isArabic ? 'تنبيهات مستوى الخزان' : 'Tank Level Alerts'}
                        </Text>
                      </View>
                      <TouchableOpacity onPress={() => setTankAlertsEnabled(!tankAlertsEnabled)}>
                        <View
                          style={[
                            styles.switch,
                            tankAlertsEnabled && { backgroundColor: theme.colors.primary },
                          ]}
                        >
                          <View
                            style={[
                              styles.switchThumb,
                              tankAlertsEnabled && styles.switchThumbActive,
                            ]}
                          />
                        </View>
                      </TouchableOpacity>
                    </View>

                    {tankAlertsEnabled && (
                      <View style={styles.row}>
                        <View style={[styles.fieldContainerSmall, styles.rowItem]}>
                          <Text style={[styles.label, { color: theme.colors.text }]}>
                            {isArabic ? 'حد منخفض (سم)' : 'Low Limit (cm)'}
                          </Text>
                          <TextInput
                            style={[
                              styles.input,
                              {
                                backgroundColor: theme.colors.card,
                                color: theme.colors.text,
                                borderColor: theme.colors.border,
                                textAlign: isRTL ? 'right' : 'left',
                              },
                            ]}
                            value={tankAlertLowCm}
                            onChangeText={(text) =>
                              handleNumericInput(text, setTankAlertLowCm, 1000)
                            }
                            placeholder={isArabic ? 'مثال: 20' : 'e.g., 20'}
                            placeholderTextColor={theme.colors.textSecondary}
                            keyboardType="numeric"
                          />
                        </View>

                        <View style={[styles.fieldContainerSmall, styles.rowItem]}>
                          <Text style={[styles.label, { color: theme.colors.text }]}>
                            {isArabic ? 'حد مرتفع (سم)' : 'High Limit (cm)'}
                          </Text>
                          <TextInput
                            style={[
                              styles.input,
                              {
                                backgroundColor: theme.colors.card,
                                color: theme.colors.text,
                                borderColor: theme.colors.border,
                                textAlign: isRTL ? 'right' : 'left',
                              },
                            ]}
                            value={tankAlertHighCm}
                            onChangeText={(text) =>
                              handleNumericInput(text, setTankAlertHighCm, 1000)
                            }
                            placeholder={isArabic ? 'مثال: 140' : 'e.g., 140'}
                            placeholderTextColor={theme.colors.textSecondary}
                            keyboardType="numeric"
                          />
                        </View>
                      </View>
                    )}

                    <Text style={[styles.hint, { color: theme.colors.textSecondary }]}>
                      {isArabic
                        ? 'يمكن ترك القيم فارغة وسيستخدم النظام افتراضيات العرض.'
                        : 'Leave empty if unknown; the system will use display defaults.'}
                    </Text>
                  </View>
                )}

                {/* Temperature Offset with Enhanced UI */}
                <View style={styles.fieldContainer}>
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 4,
                    }}
                  >
                    <Text style={[styles.label, { color: theme.colors.text, marginBottom: 0 }]}>
                      {isArabic ? 'إزاحة درجة الحرارة (°C)' : 'Temperature Offset (°C)'}
                    </Text>
                    {calibrationOffsetTemp !== '0' && calibrationOffsetTemp !== '' && (
                      <View
                        style={{
                          backgroundColor: theme.colors.info + '20',
                          paddingHorizontal: 8,
                          paddingVertical: 2,
                          borderRadius: 8,
                        }}
                      >
                        <Text style={{ color: theme.colors.info, fontSize: 11, fontWeight: '600' }}>
                          {parseFloat(calibrationOffsetTemp) > 0 ? '+' : ''}
                          {calibrationOffsetTemp}°C
                        </Text>
                      </View>
                    )}
                  </View>
                  <Text
                    style={[
                      styles.hint,
                      { fontSize: 11, marginBottom: 8, color: theme.colors.textSecondary },
                    ]}
                  >
                    {isArabic
                      ? '💡 مثال: إذا القراءة 25°س والحقيقة 23°س، أدخل -2'
                      : '💡 Example: If reading 25°C but actual 23°C, enter -2'}
                  </Text>
                  <TextInput
                    style={[
                      styles.input,
                      {
                        backgroundColor: theme.colors.background,
                        color: theme.colors.text,
                        borderColor: theme.colors.border,
                        textAlign: isRTL ? 'right' : 'left',
                      },
                    ]}
                    value={calibrationOffsetTemp}
                    onChangeText={(text) =>
                      handleSignedDecimalInput(text, setCalibrationOffsetTemp)
                    }
                    keyboardType={Platform.OS === 'ios' ? 'numbers-and-punctuation' : 'default'}
                    placeholder={isArabic ? '0.0' : '0.0'}
                    placeholderTextColor={theme.colors.textSecondary}
                  />
                </View>

                {/* Humidity Offset with Enhanced UI */}
                <View style={styles.fieldContainer}>
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 4,
                    }}
                  >
                    <Text style={[styles.label, { color: theme.colors.text, marginBottom: 0 }]}>
                      {isArabic ? 'إزاحة الرطوبة (%)' : 'Humidity Offset (%)'}
                    </Text>
                    {calibrationOffsetHumidity !== '0' && calibrationOffsetHumidity !== '' && (
                      <View
                        style={{
                          backgroundColor: theme.colors.info + '20',
                          paddingHorizontal: 8,
                          paddingVertical: 2,
                          borderRadius: 8,
                        }}
                      >
                        <Text style={{ color: theme.colors.info, fontSize: 11, fontWeight: '600' }}>
                          {parseFloat(calibrationOffsetHumidity) > 0 ? '+' : ''}
                          {calibrationOffsetHumidity}%
                        </Text>
                      </View>
                    )}
                  </View>
                  <Text
                    style={[
                      styles.hint,
                      { fontSize: 11, marginBottom: 8, color: theme.colors.textSecondary },
                    ]}
                  >
                    {isArabic
                      ? '💡 مثال: إذا القراءة 60% والحقيقة 55%، أدخل -5'
                      : '💡 Example: If reading 60% but actual 55%, enter -5'}
                  </Text>
                  <TextInput
                    style={[
                      styles.input,
                      {
                        backgroundColor: theme.colors.background,
                        color: theme.colors.text,
                        borderColor: theme.colors.border,
                        textAlign: isRTL ? 'right' : 'left',
                      },
                    ]}
                    value={calibrationOffsetHumidity}
                    onChangeText={(text) =>
                      handleSignedDecimalInput(text, setCalibrationOffsetHumidity)
                    }
                    keyboardType={Platform.OS === 'ios' ? 'numbers-and-punctuation' : 'default'}
                    placeholder={isArabic ? '0.0' : '0.0'}
                    placeholderTextColor={theme.colors.textSecondary}
                  />
                </View>
              </View>

              {/* Info Section */}
              <View
                style={[
                  styles.infoBox,
                  { backgroundColor: theme.colors.info + '20', borderColor: theme.colors.info },
                ]}
              >
                <Ionicons name="information-circle" size={24} color={theme.colors.info} />
                <Text style={[styles.infoText, { color: theme.colors.text }]}>
                  {isArabic
                    ? 'لتغيير المنطقة أو الموقع، يرجى الاتصال بمسؤول النظام'
                    : 'To change zone or site, please contact system administrator'}
                </Text>
              </View>
            </View>
          </ScrollView>

          {/* Action Buttons */}
          <View style={styles.actionButtons}>
            <TouchableOpacity
              style={[
                styles.secondaryButton,
                { backgroundColor: theme.colors.card, borderColor: theme.colors.warning },
              ]}
              onPress={() => navigation.navigate('AlarmRules', { deviceId, deviceName: name })}
            >
              <Ionicons name="notifications-outline" size={20} color={theme.colors.warning} />
              <Text style={[styles.secondaryButtonText, { color: theme.colors.warning }]}>
                {isArabic ? 'إدارة القواعد' : 'Manage Rules'}
              </Text>
            </TouchableOpacity>
          </View>

          {/* Save Button */}
          <View
            style={[
              styles.footer,
              { backgroundColor: theme.colors.card, borderTopColor: theme.colors.border },
            ]}
          >
            <TouchableOpacity
              style={[styles.saveButton, { backgroundColor: theme.colors.primary }]}
              onPress={handleSave}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator size="small" color="#ffffff" />
              ) : (
                <>
                  <Ionicons name="checkmark" size={24} color="#ffffff" />
                  <Text style={styles.saveButtonText}>
                    {isArabic ? 'حفظ التغييرات' : 'Save Changes'}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          </View>

          {/* Device Type Picker Modal — only mounted while open so it can never be
              torn down by react-native-screens mid-navigation (iOS native crash). */}
          {showDeviceTypePicker && (
            <Modal
              visible={showDeviceTypePicker}
              transparent
              animationType="slide"
              onRequestClose={() => setShowDeviceTypePicker(false)}
            >
              <TouchableOpacity
                style={styles.modalOverlay}
                activeOpacity={1}
                onPress={() => setShowDeviceTypePicker(false)}
              >
                <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
                  <View style={styles.modalHeader}>
                    <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                      {isArabic ? 'اختر نوع الجهاز' : 'Select Device Type'}
                    </Text>
                    <TouchableOpacity onPress={() => setShowDeviceTypePicker(false)}>
                      <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
                    </TouchableOpacity>
                  </View>
                  <FlatList
                    data={getAllDeviceTypes()}
                    keyExtractor={(item) => item}
                    renderItem={({ item }) => (
                      <TouchableOpacity
                        style={[
                          styles.deviceTypeItem,
                          { borderBottomColor: theme.colors.border },
                          deviceType === item && { backgroundColor: theme.colors.primary + '10' },
                        ]}
                        onPress={() => {
                          setDeviceType(item);
                          setShowDeviceTypePicker(false);
                        }}
                      >
                        <Ionicons
                          name={getDeviceTypeIcon(item) as any}
                          size={24}
                          color={
                            deviceType === item ? theme.colors.primary : theme.colors.textSecondary
                          }
                        />
                        <Text
                          style={[
                            styles.deviceTypeText,
                            {
                              color: deviceType === item ? theme.colors.primary : theme.colors.text,
                            },
                          ]}
                        >
                          {getDeviceTypeLabel(item, isArabic ? 'ar' : 'en')}
                        </Text>
                        {deviceType === item && (
                          <Ionicons
                            name="checkmark-circle"
                            size={20}
                            color={theme.colors.primary}
                          />
                        )}
                      </TouchableOpacity>
                    )}
                  />
                </View>
              </TouchableOpacity>
            </Modal>
          )}
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
  },
  content: {
    padding: 16,
  },
  fieldContainer: {
    marginBottom: 20,
  },
  fieldContainerSmall: {
    marginBottom: 12,
  },
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  rowItem: {
    flex: 1,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 14,
    fontSize: 16,
  },
  hint: {
    fontSize: 12,
    marginTop: 4,
  },
  tankConfigCard: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  tankConfigHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  tankConfigTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  unitToggleRow: {
    flexDirection: 'row',
    gap: 12,
  },
  unitToggle: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 10,
    borderWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  unitToggleText: {
    fontSize: 14,
    fontWeight: '600',
  },
  comingSoonBadge: {
    fontSize: 10,
    marginLeft: 4,
  },
  calculatedValueCard: {
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 12,
  },
  calculatedValueHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  calculatedLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
  calculatedValueRow: {
    marginLeft: 28,
  },
  calculatedValue: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 4,
  },
  calculatedValueSecondary: {
    fontSize: 13,
  },
  labelWithIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  autoCalculateBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  autoCalculateText: {
    fontSize: 11,
    fontWeight: '600',
  },
  section: {
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 8,
  },
  sectionDescription: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
  },
  infoBox: {
    flexDirection: 'row',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
    alignItems: 'flex-start',
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  footer: {
    padding: 16,
    borderTopWidth: 1,
  },
  actionButtons: {
    padding: 16,
    paddingTop: 8,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 14,
    borderRadius: 12,
    borderWidth: 1.5,
    gap: 8,
  },
  secondaryButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    gap: 8,
  },
  saveButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  pickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  pickerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  pickerText: {
    fontSize: 16,
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 20,
  },
  switchLabel: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flex: 1,
    gap: 12,
  },
  switchTextContainer: {
    flex: 1,
  },
  switch: {
    width: 50,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#ccc',
    padding: 3,
    justifyContent: 'center',
  },
  switchThumb: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#fff',
  },
  switchThumbActive: {
    alignSelf: 'flex-end',
  },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  switchLabelInline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  deviceTypeItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    gap: 12,
    borderBottomWidth: 1,
  },
  deviceTypeText: {
    flex: 1,
    fontSize: 16,
  },
});
